// Semicircle weight-progress gauge.
// SVG arc via stroke-dasharray on a circle, pink gradient fill.
// Direction-aware: swaps labels for loss vs. gain goals.
// Shows: start/current/target weight, total lost/gained, % complete, distance to goal.

import type { DashboardProgress } from '../lib/types'
import type { Strings } from '../lib/i18n'
import { kgToLb } from '../lib/units'

interface Props {
  progress: DashboardProgress
  strings: Strings
}

// Build an SVG arc path for a semicircle gauge (180°).
// r = radius, cx/cy = center, pct = 0..100 fill.
function SemicircleArc({ pct, ariaLabel, completeLabel }: { pct: number; ariaLabel: string; completeLabel: string }) {
  const r = 70
  const cx = 90
  const cy = 85
  const circumference = Math.PI * r // semicircle = half of 2πr
  const filled = Math.min(Math.max(pct / 100, 0), 1) * circumference

  // Semicircle: start at left (180°), go to right (0°) clockwise.
  const trackPath = `M ${cx - r},${cy} A ${r},${r} 0 0,1 ${cx + r},${cy}`

  return (
    <svg
      className="gauge-svg"
      viewBox={`0 0 ${cx * 2} ${cy + 20}`}
      aria-label={ariaLabel}
      role="img"
    >
      <defs>
        <linearGradient id="gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="var(--brand-primary)" />
        </linearGradient>
      </defs>
      {/* Track */}
      <path
        d={trackPath}
        fill="none"
        stroke="var(--bg-sunk)"
        strokeWidth="12"
        strokeLinecap="round"
      />
      {/* Filled arc */}
      <path
        d={trackPath}
        fill="none"
        stroke="url(#gauge-grad)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        strokeDashoffset="0"
      />
      {/* Center percent text */}
      <text
        x={cx}
        y={cy - 10}
        textAnchor="middle"
        className="gauge-pct-text"
        fill="var(--fg)"
      >
        {pct}%
      </text>
      <text
        x={cx}
        y={cy + 6}
        textAnchor="middle"
        className="gauge-pct-label"
        fill="var(--fg-muted)"
      >
        {completeLabel}
      </text>
    </svg>
  )
}

// Progress values arrive in kg; display them in pounds.
function fmt(kg: number) {
  return kgToLb(kg).toFixed(1)
}

export function ProgressGauge({ progress, strings: s }: Props) {
  // No goal weight on file: show the honest trend card. A gauge needs a
  // target; inventing one (old behavior: target = current) told everyone
  // with any net loss that they were 100% done.
  if (progress.direction === 'no_goal' || progress.target_kg == null) {
    return (
      <div className="dash-card dash-gauge-card">
        <div className="dash-card-title">{s.progressTitleNoGoal}</div>
        <div className="gauge-nogoal-headline">
          <span className="gauge-stat-value gauge-stat-current">{fmt(progress.changed_kg)} lb</span>
          <span className="gauge-stat-label">{s.progressChangedLoss}</span>
        </div>
        <div className="gauge-stats">
          <div className="gauge-stat">
            <span className="gauge-stat-value">{fmt(progress.start_kg)}</span>
            <span className="gauge-stat-label">{s.progressStartLabel}</span>
          </div>
          <div className="gauge-stat gauge-stat-center">
            <span className="gauge-stat-value gauge-stat-current">{fmt(progress.current_kg)}</span>
            <span className="gauge-stat-label">{s.progressCurrentLabel}</span>
          </div>
        </div>
        <div className="gauge-nogoal-hint">{s.progressNoGoalHint}</div>
      </div>
    )
  }

  const isGain = progress.direction === 'gain'
  const title = isGain ? s.progressTitleGain : s.progressTitleLoss
  const changedLabel = isGain ? s.progressChangedGain : s.progressChangedLoss
  const pct = progress.pct_complete ?? 0
  const remainingKg = progress.remaining_kg ?? 0

  return (
    <div className="dash-card dash-gauge-card">
      <div className="dash-card-title">{title}</div>
      <SemicircleArc
        pct={pct}
        ariaLabel={`${title} ${pct}%`}
        completeLabel={s.progressComplete}
      />

      <div className="gauge-stats">
        <div className="gauge-stat">
          <span className="gauge-stat-value">{fmt(progress.start_kg)}</span>
          <span className="gauge-stat-label">{s.progressStartLabel}</span>
        </div>
        <div className="gauge-stat gauge-stat-center">
          <span className="gauge-stat-value gauge-stat-current">{fmt(progress.current_kg)}</span>
          <span className="gauge-stat-label">{s.progressCurrentLabel}</span>
        </div>
        <div className="gauge-stat">
          <span className="gauge-stat-value">{fmt(progress.target_kg)}</span>
          <span className="gauge-stat-label">{s.progressTargetLabel}</span>
        </div>
      </div>

      <div className="gauge-summary">
        <div className="gauge-summary-item">
          <span className="gauge-summary-key">{changedLabel}</span>
          <span className="gauge-summary-val">{fmt(progress.changed_kg)} lb</span>
        </div>
        <div className="gauge-summary-item">
          <span className="gauge-summary-key">{s.progressToGoal}</span>
          <span className="gauge-summary-val">{fmt(remainingKg)} lb</span>
        </div>
      </div>
    </div>
  )
}
